import { useEffect, useState } from "react";
import { ArrowLeft, Calendar, ChevronRight, FileBadge, MapPin, Pencil, RefreshCw, Trophy, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function GestioneCertificati() {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [renaming, setRenaming] = useState(null); // {compId, athlete}
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const load = async (manual = false) => {
    if (manual) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get("/certificates");
      setCompetitions(data);
      if (manual) toast.success("Lista aggiornata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openRename = (compId, a) => {
    setRenaming({ compId, athlete: a });
    setNewName(a.name || "");
  };

  const saveRename = async () => {
    const v = newName.trim();
    if (!v) { toast.error("Il nome non può essere vuoto"); return; }
    setSavingName(true);
    try {
      const { data } = await api.put(`/competitions/${renaming.compId}/athletes/${renaming.athlete.id}/rename`, { name: v });
      toast.success("Nome aggiornato");
      setCompetitions((cs) => cs.map((c) => c.id !== renaming.compId ? c : ({
        ...c,
        athletes: (c.athletes || []).map((x) => x.id === renaming.athlete.id ? { ...x, name: data.name } : x),
      })));
      setRenaming(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-2">Gestione Certificati</h1>
          <p className="text-slate-500 mt-2">Tutti gli attestati emessi raggruppati per competizione. Per rigenerare un attestato apri la competizione dalla sezione Risultati.</p>
        </div>
        <Button onClick={() => load(true)} disabled={refreshing} className="bg-blue-700 hover:bg-blue-800 text-white h-11 px-5" data-testid="refresh-certificates-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      ) : competitions.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm" data-testid="certificates-empty">
          Nessun attestato emesso. Le competizioni inviate compariranno qui con la lista degli atleti.
        </div>
      ) : (
        <div className="space-y-3" data-testid="certificates-list">
          {competitions.map((c) => {
            const isOpen = openId === c.id;
            const athletes = c.athletes || [];
            return (
              <div key={c.id} className="bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden" data-testid={`cert-comp-${c.id}`}>
                <button
                  onClick={() => setOpenId(isOpen ? null : c.id)}
                  className="w-full text-left px-5 sm:px-6 py-4 flex items-center gap-4 hover:bg-slate-50 transition"
                  data-testid={`cert-comp-toggle-${c.id}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center flex-shrink-0">
                    <Trophy className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-semibold text-slate-900 truncate">{c.name}</h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                      <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{c.location}</span>
                      <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(c.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</span>
                      <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{athletes.length} attestat{athletes.length === 1 ? "o" : "i"}</span>
                    </div>
                  </div>
                  <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 hidden sm:inline-flex">
                    <FileBadge className="h-3.5 w-3.5 mr-1" /> {athletes.length}
                  </Badge>
                  <ChevronRight className={`h-5 w-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-slate-200 bg-slate-50/60 px-5 sm:px-6 py-4" data-testid={`cert-comp-list-${c.id}`}>
                    {athletes.length === 0 ? (
                      <p className="text-sm text-slate-500 italic">Nessun atleta presente in questa competizione.</p>
                    ) : (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {athletes.map((a) => (
                          <li
                            key={a.id}
                            className="flex items-center gap-3 bg-white rounded-lg ring-1 ring-slate-200 px-3 py-2.5"
                            data-testid={`cert-athlete-${a.id}`}
                          >
                            <span className="font-mono font-bold text-blue-700 text-sm w-9 text-center flex-shrink-0">{a.number}</span>
                            <span className="font-medium text-slate-900 text-sm truncate flex-1">{a.name}</span>
                            {a.type && (
                              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex-shrink-0">{a.type}</span>
                            )}
                            <button
                              type="button"
                              onClick={() => openRename(c.id, a)}
                              className="flex-shrink-0 text-blue-700 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="Modifica nome atleta"
                              data-testid={`rename-athlete-${a.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={renaming !== null} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="sm:max-w-md" data-testid="rename-athlete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Modifica nome atleta</DialogTitle>
            <DialogDescription>
              {renaming && (
                <>Numero <span className="font-bold text-blue-700">#{renaming.athlete.number}</span>. Il nome verrà salvato in maiuscolo. Dopo la modifica potrai rigenerare l'attestato.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nuovo nome"
              data-testid="rename-input"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRenaming(null)} disabled={savingName} data-testid="rename-cancel">Annulla</Button>
            <Button onClick={saveRename} disabled={savingName} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="rename-save">
              {savingName ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
